import type { CSSProperties, ElementType, ReactNode } from "react"

import { cn } from "@/lib/utils"

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

const TEXT_GOLD_STYLE: CSSProperties = {
  color: "var(--accent)",
  textShadow:
    "0 0 6px oklch(0.8 0.16 85 / 0.3), 0 0 16px oklch(0.7 0.14 85 / 0.12), 0 0 24px oklch(0.8 0.3 145 / 0.2), 0 1px 0 oklch(0.55 0.1 85), 0 2px 4px oklch(0 0 0 / 0.5)",
}

const TEXT_GOLD_SM_STYLE: CSSProperties = {
  color: "var(--accent)",
  textShadow:
    "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
}

const sizeMap: Record<
  HeadingLevel,
  { className: string; style?: CSSProperties }
> = {
  1: {
    className: "font-display text-4xl font-bold leading-none",
    style: TEXT_GOLD_STYLE,
  },
  2: {
    className: "font-display text-xl font-semibold tracking-tight",
    style: TEXT_GOLD_SM_STYLE,
  },
  3: {
    className: "font-display text-lg font-medium",
    style: TEXT_GOLD_SM_STYLE,
  },
  4: { className: "text-base font-medium" },
  5: { className: "text-sm font-medium" },
  6: { className: "text-xs font-medium uppercase tracking-wide" },
}

interface HeadingProps {
  level?: HeadingLevel
  children: ReactNode
  className?: string
  wrapperClassName?: string
  description?: string
}

export function Heading({
  level = 2,
  children,
  className,
  wrapperClassName,
  description,
}: HeadingProps) {
  const Tag = `h${level}` as ElementType
  const { className: sizeClass, style } = sizeMap[level]

  return (
    <div
      data-slot="heading"
      className={cn("mb-8 space-y-0.5", wrapperClassName)}
    >
      <Tag className={cn(sizeClass, className)} style={style}>
        {children}
      </Tag>
      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
    </div>
  )
}
